/**
 * arbi agent create       — create a persistent agent
 * arbi agent list         — list persistent agents
 * arbi agent delete <id>  — delete an agent
 */

import { Command } from 'commander'
import { agents as agentOps, generateEncryptedWorkspaceKey } from '@arbidocs/sdk'
import { initSodium, generateLoginCredentials, bytesToBase64 } from '@arbidocs/client'
import {
  runAction,
  resolveAuth,
  printTable,
  printJson,
  requireInteractive,
  suggestSubcommandAndExit,
  dryRun,
  formatUserName,
  type UserInfo,
} from '../helpers.js'
import { promptSelect, promptConfirm, promptInput, promptCheckbox } from '../prompts.js'
import { success, ref, dim } from '../output.js'
import { store, resolveConfig } from '../config.js'

/** Generate a random URL-safe password (20 chars). */
function generatePassword(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_'
  const bytes = new Uint8Array(20)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => chars[b % chars.length]).join('')
}

export function registerAgentCommand(program: Command): void {
  const agent = program
    .command('agent')
    .description(
      'Child agents under your user: list, create, delete ' +
        '(for SUPPORT_API_KEY bot accounts, use `arbi agent-create`)'
    )

  // ── create ────────────────────────────────────────────────────────────────
  agent
    .command('create')
    .description(
      'Create a child persistent agent owned by the current user (different from ' +
        '`arbi agent-create`, which is a SUPPORT_API_KEY bot-account flow)'
    )
    .option('--name <name>', 'Agent name (letters, digits, hyphens, underscores)')
    .option('-w, --workspace <ids...>', 'Workspace IDs to share with the agent')
    .option('--role <role>', 'Workspace role: collaborator or guest', 'collaborator')
    .action((opts: { name?: string; workspace?: string[]; role?: string }) =>
      runAction(async () => {
        const { arbi, loginResult } = await resolveAuth()
        const config = resolveConfig()
        const creds = store.requireCredentials()

        let name = opts.name
        if (!name) name = await promptInput('Agent name (letters, digits, hyphens, underscores)')
        if (!name?.trim() || !/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(name.trim())) {
          throw new Error('Invalid agent name — use letters, digits, hyphens, underscores')
        }

        // Resolve workspaces to share (optional)
        let workspaceIds = opts.workspace ?? []
        if (workspaceIds.length === 0) {
          const { data: allWorkspaces } = await arbi.fetch.GET('/v1/user/workspaces')
          if (allWorkspaces && allWorkspaces.length > 0) {
            workspaceIds = await promptCheckbox(
              'Share with workspaces (optional — press Enter to skip)',
              allWorkspaces.map((ws) => ({
                name: `${ws.name} (${ws.external_id})`,
                value: ws.external_id,
              }))
            )
          }
        }

        // Derive keypair from email + password, same as human registration.
        // Agent email: name.usr-XXXXXXXX@domain — use session state for parent ext_id.
        await initSodium()
        const parentExtId = arbi.session.getState().userExtId ?? ''
        if (!parentExtId) throw new Error('Could not determine current user ext_id')

        const agentEmail = `${name.trim()}.${parentExtId}@${config.deploymentDomain}`
        const password = generatePassword()

        const { signingPrivateKey } = await generateLoginCredentials(
          { email: agentEmail },
          password,
          config.deploymentDomain
        )
        const signingPubKey = bytesToBase64(signingPrivateKey.slice(32))

        const { data: agent, error } = await arbi.fetch.POST('/v1/user/agent', {
          body: {
            name: name.trim(),
            signing_key: signingPubKey,
            recovery_key: bytesToBase64(signingPrivateKey),
          },
        })
        if (!agent) throw new Error(`Failed to create agent: ${JSON.stringify(error)}`)

        // Share selected workspaces
        if (workspaceIds.length > 0) {
          const { data: allWs } = await arbi.fetch.GET('/v1/user/workspaces')
          for (const wsId of workspaceIds) {
            const ws = allWs?.find((w) => w.external_id === wsId)
            if (!ws?.wrapped_key) {
              dim(`Skipping ${wsId} — no key found`)
              continue
            }
            const encryptedKey = await generateEncryptedWorkspaceKey(
              arbi,
              ws.wrapped_key,
              loginResult.serverSessionKey,
              creds.signingPrivateKeyBase64
            )
            await arbi.fetch.POST('/v1/workspace/users', {
              body: {
                emails: [agent.email],
                role: (opts.role ?? 'collaborator') as 'collaborator' | 'guest',
                workspace_key: encryptedKey,
                workspace_ext_id: wsId,
              },
            })
          }
        }

        console.log('')
        success('Agent created!')
        console.log('')
        console.log(`  Email:    ${ref(agent.email)}`)
        console.log(`  Password: ${ref(password)}`)
        if (workspaceIds.length > 0) console.log(`  Workspaces: ${workspaceIds.join(', ')}`)
        console.log('')
        dim(`On the agent machine: arbi login --email ${agent.email} --password <password>`)
      })()
    )

  // ── list ─────────────────────────────────────────────────────────────────
  agent
    .command('list')
    .description('List your persistent agents')
    .option('--json', 'Output as JSON (recommended for scripting / agents)')
    .action((opts?: { json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await agentOps.listAgents(arbi)

        if (opts?.json) {
          printJson(data)
          return
        }

        if (data.length === 0) {
          process.stderr.write('No agents found.\n')
          return
        }

        printTable(
          [
            { header: 'ID', width: 20, value: (r) => r.external_id as string },
            {
              header: 'NAME',
              width: 24,
              value: (r) => formatUserName(r as unknown as UserInfo) || (r.email as string) || '',
            },
            { header: 'EMAIL', width: 40, value: (r) => (r.email as string) || '' },
          ],
          data
        )
      })()
    )

  // ── delete ───────────────────────────────────────────────────────────────
  agent
    .command('delete [agent-id]')
    .description('Delete a persistent agent (picker if no ID)')
    .option('-y, --yes', 'Skip confirmation (required in non-TTY shells)')
    .option('--dry-run', 'Preview which agent would be deleted (no SDK call)')
    .action((agentId: string | undefined, opts: { yes?: boolean; dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()

        if (!agentId) {
          const data = await agentOps.listAgents(arbi)
          if (data.length === 0) {
            process.stderr.write('No agents to delete.\n')
            return
          }
          requireInteractive('Pass <agent-id> directly: arbi agent delete <id>')
          agentId = await promptSelect(
            'Select agent to delete',
            data.map((a) => ({
              name: `${formatUserName(a as unknown as UserInfo) || a.email} (${a.external_id})`,
              value: a.external_id,
            }))
          )
        }

        if (opts?.dryRun) {
          dryRun('delete agent', agentId)
          return
        }

        if (!opts?.yes) {
          requireInteractive('Pass -y/--yes to confirm in non-TTY shells.')
          const confirmed = await promptConfirm(`Delete agent ${agentId}?`, false)
          if (!confirmed) {
            console.log('Cancelled.')
            return
          }
        }

        await agentOps.deleteAgents(arbi, [agentId])
        success(`Agent ${agentId} deleted.`)
      })()
    )

  // Default action: list, OR suggest a subcommand if user typed a typo.
  agent.arguments('[maybeSubcommand]').action(async (maybe?: string) => {
    if (maybe) {
      suggestSubcommandAndExit(
        'agent',
        maybe,
        agent.commands.map((c) => c.name())
      )
    }
    await agent.commands.find((c) => c.name() === 'list')!.parseAsync([], { from: 'user' })
  })
}
